import { NotificationTypes, type SafeAny } from '@/common/types';
import type { EpochParams } from '@/modules/epoch/types';
import { getStudyData } from '@/modules/study/data-access';
import { loadEpochData } from './signalHelpers';

const FULL_NIGHT_EPOCH_SIZE = -1;

export const getFullNightSignalData = async (store: SafeAny, payload: EpochParams) => {
  const { rootState, state, commit } = store;
  const { id, band, notch } = payload || {};

  try {
    state.fullNight.isLoading = true;
    state.fullNight.error = undefined;

    if (
      !rootState.study.config.data?.sets?.[FULL_NIGHT_EPOCH_SIZE] ||
      payload?.id !== rootState.study.config.data?.id ||
      payload?.band !== rootState.study.config.data?.band ||
      payload?.notch !== rootState.study.config.data?.notch
    ) {
      await getStudyData(store, {
        id,
        epochSize: FULL_NIGHT_EPOCH_SIZE,
        notch,
        band
      } as any);
    }

    const epochSet = rootState.study.config.data?.sets?.[FULL_NIGHT_EPOCH_SIZE] || [];
    const epochKeys = Object.keys(epochSet).sort((a, b) => Number(a) - Number(b));
    if (epochKeys.length === 0) {
      state.fullNight.isLoading = false;
      state.fullNight.data = undefined;
      return;
    }
    const epochKey = epochKeys[0] ?? '0';
    const abortController = new AbortController();

    const resolved = await loadEpochData(
      epochKey,
      FULL_NIGHT_EPOCH_SIZE,
      state,
      rootState,
      abortController.signal,
      commit
    );

    state.fullNight.data = resolved
      ? { signal: resolved.signal, mapping: resolved.mapping }
      : undefined;
    state.fullNight.isLoading = false;
  } catch (error: SafeAny) {
    state.fullNight.isLoading = false;
    state.fullNight.data = undefined;
    // Mirror the convention used by other epoch resource actions (see e.g.
    // `get-signal-data.ts` line 162). Without this, consumers that watch
    // `state.fullNight.error` to detect failures (e.g. the cold-load latch
    // in `epoch.view.vue`) would never see the failure and could wait
    // indefinitely for `state.fullNight.data` that's never going to land.
    state.fullNight.error = error;

    commit(
      'addNotification',
      {
        i18nTitle: 'errorGetSignalData',
        i18nDescription: error.message,
        type: NotificationTypes.Error
      },
      { root: true }
    );
  }
};
